/**
 * ColorPickerPalette  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { ISchemaStringWithConstConstItem } from "@adapptio/lib-blueprint";
import { ColorPickerPaletteTile, IColorPickerPaletteTileProps } from "./ColorPickerPaletteTile";
import { useColorPickerContext } from "./ColorPickerContext";

export interface IColorPickerPaletteProps {
	items?: ISchemaStringWithConstConstItem[];
	className?: string;
	onChange?: (value: string) => void;
}

export const ColorPickerPalette: React.FC<IColorPickerPaletteProps> = ({ className, items, onChange }) => {
	const { palette, onChange: globalOnChange, value, onClose } = useColorPickerContext();
	const baseClass = BEM("edt-color-picker-palette");

	const paletteItems = items || palette;

	const handleTileClick = useCallback<IColorPickerPaletteTileProps["onClick"]>(
		(value) => {
			if (isFunction(onChange)) {
				onChange(value);
			}

			if (isFunction(globalOnChange)) {
				globalOnChange(value);
			}

			if (isFunction(onClose)) {
				onClose();
			}
		},
		[ onChange, globalOnChange ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			{paletteItems?.length > 0 && (
				<div className={baseClass.element("block").toClassName()}>
					<div className={baseClass.element("tiles").toClassName()}>
						{paletteItems?.map((item, index) => (
							<ColorPickerPaletteTile
								key={`palette-tile-custom-${index}`}
								value={item.value}
								label={item.label}
								onClick={handleTileClick}
								selected={value === item.label}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
